import { useState, useMemo } from "react";
import axios from "axios";
import { X, Compass, Briefcase, IndianRupee, ArrowRight, Upload, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authH = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("asg_token")}` } });

const VERTS = [
  { key: "yatra", label: "Yatra", icon: <Compass size={16} />, color: "#B45309", bg: "#FEF3C7" },
  { key: "jobs", label: "Jobs", icon: <Briefcase size={16} />, color: "#1D4ED8", bg: "#DBEAFE" },
  { key: "loans", label: "Loans", icon: <IndianRupee size={16} />, color: "#0F7A4E", bg: "#D1FAE5" },
];

const SOURCES = ["Manual", "Website", "Google", "Google Ads", "Facebook", "Instagram", "WhatsApp", "Google Sheet", "Referral", "Job Application", "Resume Service", "Other"];
const STATUSES = ["New", "Contacted", "Not Contacted", "Customer Asked to Call Later", "Interested", "Follow-up Required", "Converted", "Lost"];
const REQUIRES_FU = new Set(["Contacted", "Not Contacted", "Customer Asked to Call Later", "Follow-up Required"]);

export default function CreateLeadModal({ onClose, onCreated }) {
  const [vertical, setVertical] = useState("yatra");
  const [form, setForm] = useState({
    name: "", mobile: "", whatsapp: "", email: "", city: "",
    source: "Manual", initial_status: "New", notes: "",
    next_followup_date: "", next_followup_time: "",
    // yatra
    package: "", departure: "", adults: "", children: "", travel_class: "", budget: "", message: "",
    // jobs
    qualification: "", experience_years: "", current_designation: "",
    preferred_role: "", preferred_location: "", expected_salary: "", notice_period: "",
    job_title: "", cover_note: "",
    resume_path: "", resume_filename: "",
    // loans
    loan_type: "", employment_type: "", monthly_income: "",
    required_amount: "", tenure: "", existing_loan: "",
  });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const missingFu = useMemo(() => (
    REQUIRES_FU.has(form.initial_status) && (!form.next_followup_date || !form.next_followup_time)
  ), [form.initial_status, form.next_followup_date, form.next_followup_time]);

  const uploadResume = async (file) => {
    if (!file) return;
    setUploading(true); setErr("");
    try {
      const fd = new FormData(); fd.append("kind", "resume"); fd.append("file", file);
      const r = await axios.post(`${API}/admin/upload`, fd, { ...authH(), headers: { ...authH().headers, "Content-Type": "multipart/form-data" } });
      set("resume_path", r.data.path); set("resume_filename", r.data.filename);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Resume upload failed");
    } finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (!form.name || !form.mobile) { setErr("Name and mobile are required"); return; }
    if (missingFu) { setErr("Please select follow-up date and time before saving this status"); return; }
    setSaving(true);
    try {
      const payload = { vertical, ...form,
        adults: form.adults ? parseInt(form.adults, 10) : null,
        children: form.children ? parseInt(form.children, 10) : null };
      const r = await axios.post(`${API}/admin/crm/leads`, payload, authH());
      setOk(true); onCreated?.(r.data);
      setTimeout(() => onClose?.(), 1500);
    } catch (e) { setErr(e?.response?.data?.detail || "Could not create lead"); }
    finally { setSaving(false); }
  };

  if (ok) return (
    <div className="crm-drawer-backdrop" onClick={onClose}>
      <div className="crm-drawer" onClick={e => e.stopPropagation()} style={{ padding: "60px 32px", textAlign: "center" }}>
        <div style={{ width: 60, height: 60, borderRadius: 999, background: "#0F7A4E", color: "#fff", display: "grid", placeItems: "center", margin: "0 auto 18px" }}>
          <CheckCircle2 size={30} />
        </div>
        <h3 style={{ fontFamily: "'Playfair Display',serif", fontSize: 24, margin: "0 0 8px" }}>Lead created</h3>
        <p style={{ color: "#64748B" }}>The new {vertical} lead is now in your CRM.</p>
      </div>
    </div>
  );

  return (
    <div className="crm-drawer-backdrop" onClick={onClose} data-testid="create-lead-modal">
      <div className="crm-drawer" onClick={e => e.stopPropagation()}>
        <button className="crm-drawer-close" onClick={onClose} data-testid="create-lead-close"><X size={18} /></button>
        <div className="crm-drawer-head">
          <span className="crm-lead-tag" style={{ background: "#F1F5F9", color: "#0B1220" }}>+ NEW LEAD</span>
          <h3>Create a lead manually</h3>
          <p style={{ color: "#64748B", fontSize: 13.5, margin: "6px 0 0" }}>Pick the vertical, then fill vertical-specific fields.</p>
        </div>

        <form onSubmit={submit} data-testid="create-lead-form">
          {err && <div className="crm-form-error" data-testid="create-lead-error">{err}</div>}

          <section className="crm-block">
            <h4>Vertical</h4>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              {VERTS.map(v => (
                <button
                  type="button" key={v.key}
                  onClick={() => setVertical(v.key)}
                  className={`crm-chip ${vertical === v.key ? "active" : ""}`}
                  style={vertical === v.key ? { background: v.color, borderColor: v.color, color: "#fff" } : {}}
                  data-testid={`create-vertical-${v.key}`}
                >
                  {v.icon} <span style={{ marginLeft: 6 }}>{v.label}</span>
                </button>
              ))}
            </div>
          </section>

          <section className="crm-block">
            <h4>Contact</h4>
            <div className="crm-form-row">
              <div><label>Full name *</label><input value={form.name} onChange={e => set("name", e.target.value)} required data-testid="create-input-name" /></div>
              <div><label>Mobile *</label><input value={form.mobile} onChange={e => set("mobile", e.target.value)} required data-testid="create-input-mobile" /></div>
            </div>
            <div className="crm-form-row">
              <div><label>WhatsApp</label><input value={form.whatsapp} onChange={e => set("whatsapp", e.target.value)} data-testid="create-input-whatsapp" /></div>
              <div><label>Email</label><input type="email" value={form.email} onChange={e => set("email", e.target.value)} data-testid="create-input-email" /></div>
            </div>
            <label>City</label>
            <input value={form.city} onChange={e => set("city", e.target.value)} data-testid="create-input-city" />
          </section>

          {vertical === "yatra" && (
            <section className="crm-block" data-testid="create-fields-yatra">
              <h4>Yatra details</h4>
              <label>Package</label>
              <input placeholder="e.g. Char Dham Yatra" value={form.package} onChange={e => set("package", e.target.value)} data-testid="create-yatra-package" />
              <label>Departure</label>
              <input placeholder="e.g. 15 May 2027" value={form.departure} onChange={e => set("departure", e.target.value)} data-testid="create-yatra-departure" />
              <div className="crm-form-row">
                <div><label>Adults</label><input type="number" min="0" value={form.adults} onChange={e => set("adults", e.target.value)} data-testid="create-yatra-adults" /></div>
                <div><label>Children</label><input type="number" min="0" value={form.children} onChange={e => set("children", e.target.value)} data-testid="create-yatra-children" /></div>
              </div>
              <div className="crm-form-row">
                <div><label>Preferred class</label>
                  <select value={form.travel_class} onChange={e => set("travel_class", e.target.value)} data-testid="create-yatra-class">
                    <option value="">Select…</option><option>Sleeper</option><option>3AC</option><option>2AC</option><option>1AC</option><option>Flight</option>
                  </select>
                </div>
                <div><label>Budget</label><input placeholder="₹" value={form.budget} onChange={e => set("budget", e.target.value)} data-testid="create-yatra-budget" /></div>
              </div>
              <label>Message</label>
              <textarea value={form.message} onChange={e => set("message", e.target.value)} data-testid="create-yatra-message" />
            </section>
          )}

          {vertical === "jobs" && (
            <section className="crm-block" data-testid="create-fields-jobs">
              <h4>Candidate details</h4>
              <div className="crm-form-row">
                <div><label>Qualification</label><input value={form.qualification} onChange={e => set("qualification", e.target.value)} data-testid="create-jobs-qualification" /></div>
                <div><label>Total experience</label><input placeholder="e.g. 3 years" value={form.experience_years} onChange={e => set("experience_years", e.target.value)} data-testid="create-jobs-experience" /></div>
              </div>
              <div className="crm-form-row">
                <div><label>Current designation</label><input value={form.current_designation} onChange={e => set("current_designation", e.target.value)} data-testid="create-jobs-designation" /></div>
                <div><label>Preferred role</label><input value={form.preferred_role} onChange={e => set("preferred_role", e.target.value)} data-testid="create-jobs-role" /></div>
              </div>
              <div className="crm-form-row">
                <div><label>Preferred location</label><input value={form.preferred_location} onChange={e => set("preferred_location", e.target.value)} data-testid="create-jobs-location" /></div>
                <div><label>Expected salary</label><input placeholder="e.g. ₹4 LPA" value={form.expected_salary} onChange={e => set("expected_salary", e.target.value)} data-testid="create-jobs-salary" /></div>
              </div>
              <div className="crm-form-row">
                <div><label>Notice period</label><input placeholder="e.g. 30 days" value={form.notice_period} onChange={e => set("notice_period", e.target.value)} data-testid="create-jobs-notice" /></div>
                <div><label>Job interested in</label><input placeholder="Job title" value={form.job_title} onChange={e => set("job_title", e.target.value)} data-testid="create-jobs-title" /></div>
              </div>
              <label><Upload size={11} style={{marginRight:4,marginBottom:-1}} />Resume (PDF, DOC, DOCX, JPG, JPEG, PNG)</label>
              <input type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                     onChange={e => uploadResume(e.target.files?.[0])} disabled={uploading} data-testid="create-jobs-resume" />
              {uploading && <small style={{ color: "#64748B" }}>Uploading…</small>}
              {form.resume_filename && !uploading && <small style={{ color: "#0F7A4E", display: "block", marginTop: 6 }}>✓ Uploaded: {form.resume_filename}</small>}
              <label>Cover note</label>
              <textarea value={form.cover_note} onChange={e => set("cover_note", e.target.value)} data-testid="create-jobs-note" />
            </section>
          )}

          {vertical === "loans" && (
            <section className="crm-block" data-testid="create-fields-loans">
              <h4>Loan details</h4>
              <div className="crm-form-row">
                <div><label>Loan type</label>
                  <select value={form.loan_type} onChange={e => set("loan_type", e.target.value)} data-testid="create-loans-type">
                    <option value="">Select…</option>
                    <option>Personal Loan</option><option>Home Loan</option><option>Business Loan</option>
                    <option>Education Loan</option><option>Vehicle Loan</option><option>Loan Against Property</option>
                  </select>
                </div>
                <div><label>Employment type</label>
                  <select value={form.employment_type} onChange={e => set("employment_type", e.target.value)} data-testid="create-loans-employment">
                    <option value="">Select…</option>
                    <option>Salaried</option><option>Self-employed</option><option>Business owner</option><option>Student</option><option>Other</option>
                  </select>
                </div>
              </div>
              <div className="crm-form-row">
                <div><label>Monthly income</label><input placeholder="₹" value={form.monthly_income} onChange={e => set("monthly_income", e.target.value)} data-testid="create-loans-income" /></div>
                <div><label>Required loan amount</label><input placeholder="₹" value={form.required_amount} onChange={e => set("required_amount", e.target.value)} data-testid="create-loans-amount" /></div>
              </div>
              <div className="crm-form-row">
                <div><label>Approximate tenure</label><input placeholder="e.g. 5 years" value={form.tenure} onChange={e => set("tenure", e.target.value)} data-testid="create-loans-tenure" /></div>
                <div><label>Existing loan</label><input placeholder="e.g. Home loan ₹10L" value={form.existing_loan} onChange={e => set("existing_loan", e.target.value)} data-testid="create-loans-existing" /></div>
              </div>
              <label>Message</label>
              <textarea value={form.message} onChange={e => set("message", e.target.value)} data-testid="create-loans-message" />
            </section>
          )}

          <section className="crm-block">
            <h4>Status, source & follow-up</h4>
            <div className="crm-form-row">
              <div><label>Source</label>
                <select value={form.source} onChange={e => set("source", e.target.value)} data-testid="create-input-source">
                  {SOURCES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div><label>Initial status</label>
                <select value={form.initial_status} onChange={e => set("initial_status", e.target.value)} data-testid="create-input-status">
                  {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
            {REQUIRES_FU.has(form.initial_status) && (
              <div className="crm-followup-req" data-testid="create-followup-req">
                <b>Follow-up mandatory for this status.</b><span>Set date + time to save.</span>
              </div>
            )}
            <div className="crm-form-row">
              <div><label>Follow-up date</label><input type="date" value={form.next_followup_date} onChange={e => set("next_followup_date", e.target.value)} data-testid="create-followup-date" /></div>
              <div><label>Follow-up time</label><input type="time" value={form.next_followup_time} onChange={e => set("next_followup_time", e.target.value)} data-testid="create-followup-time" /></div>
            </div>
            <label>Notes</label>
            <textarea placeholder="Anything to remember" value={form.notes} onChange={e => set("notes", e.target.value)} data-testid="create-input-notes" />
          </section>

          <button type="submit" className="crm-save" disabled={saving || missingFu} data-testid="create-lead-submit">
            {saving ? "Creating…" : <>Create lead <ArrowRight size={14} style={{ marginLeft: 6 }} /></>}
          </button>
        </form>
      </div>
    </div>
  );
}
